import React from "react";
import PropTypes from "prop-types";
import {
	Table as SemanticTable,
	TableHeader,
	TableRow,
	TableHeaderCell,
	TableBody,
	TableCell,
	TableFooter
} from "semantic-ui-react";
import { isNonEmptyString, isNode } from "@cayuse/shared-react.utils.type-checks";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";
import TableErrorRow from "components/Table/ErrorRow";
import TableExpandableRow from "components/Table/ExpandableRow";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import SubHeader from "@cayuse/shared-react.components.sub-header";

import styles from "./styles.css";


const Table = ({
	children,
	header,
	isLoading,
	error,
	overflow,
	noBorders,
	noContentBorders,
	className,
	captionText,
	...rest
}) => {
	const hasStringHeader = isNonEmptyString(header);
	const hasNodeHeader = isNode(header);
	const hasHeader = hasStringHeader || hasNodeHeader;
	const isOverflowVisible = overflow === "visible";

	const innerTableClasses = crunchClasses({
		[styles.liveTable]: true,
		[styles.noBorders]: !!noBorders,
		[styles.overflowVisible]: !!isOverflowVisible,
		[className]: isNonEmptyString(className)
	});

	const renderInnerTable = () => (
		<SemanticTable className={innerTableClasses} {...rest} >
			{captionText && (
				<caption>
					{captionText}
				</caption>
			)}
			{children}
		</SemanticTable>
	);

	const contentWrapperClasses = crunchClasses({
		[styles.contentWrapper]: true,
		[styles.withHeader]: hasHeader,
		[styles.overflowVisible]: overflow === "visible",
		[styles.noContentBorders]: !!noContentBorders || !!noBorders,
		[styles.noBorders]: !!noBorders
	});

	return isLoading
		? <LoadingIndicator />
		: (
			<div data-component="Table" className={styles.table}>
				<div className={contentWrapperClasses}>
					{hasHeader &&
				<div className={styles.heading}>
					{hasStringHeader && <SubHeader><h2>{header}</h2></SubHeader>}
					{hasNodeHeader && header}
				</div>
					}

					<div className={`${styles.tableWrapper} ${isOverflowVisible ? styles.overflowVisible : ""}`}>
						{renderInnerTable()}
					</div>
				</div>

			</div>
		);
};

Table.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	error: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.string
	]),
	header: PropTypes.oneOfType([
		PropTypes.node,
		PropTypes.string
	]),
	isLoading: PropTypes.bool,
	overflow: PropTypes.oneOf([ "visible", "hidden" ]),
	noBorders: PropTypes.bool,
	noContentBorders: PropTypes.bool,
	captionText: PropTypes.string
};

/* Wire other components onto Table to emulate Semantic-UI's export */
Table.Header = TableHeader;
Table.Row = TableRow;
Table.HeaderCell = TableHeaderCell;
Table.Body = TableBody;
Table.Cell = TableCell;
Table.Footer = TableFooter;
Table.ErrorRow = TableErrorRow;
Table.ExpandableRow = TableExpandableRow;

export default Table;
